import React, { useState, useRef } from 'react';
import { Landmark, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import FileUploadSection from './FileUploadSection';
import { inputClass, labelClass } from './vaultUtils';

const BankEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    bank_name: entry?.data?.bank_name || '',
    ifsc_code: entry?.data?.ifsc_code || '',
    account_number: entry?.data?.account_number || '',
    joint_holder_1: entry?.data?.joint_holder_1 || '',
    joint_holder_2: entry?.data?.joint_holder_2 || '',
    nominee_1: entry?.data?.nominee_1 || '',
    nominee_2: entry?.data?.nominee_2 || '',
    nominee_3: entry?.data?.nominee_3 || '',
    contact_person_name: entry?.data?.contact_person_name || '',
    contact_person_mobile: entry?.data?.contact_person_mobile || ''
  });

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Auto uppercase IFSC
    if (name === 'ifsc_code') {
      processedValue = value.toUpperCase();
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name to save as');
      return;
    }
    if (!formData.bank_name.trim()) {
      toast.error('Please enter the bank name');
      return;
    }
    
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="bank-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Landmark className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Bank Details' : 'Edit Bank Details'}
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          {/* File Upload Section */}
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          {/* Save As */}
          <div className="space-y-2">
            <Label className={labelClass}>Save As *</Label>
            <Input
              name="save_as"
              value={formData.save_as}
              onChange={handleChange}
              placeholder="e.g., HDFC Salary Account, SBI Savings"
              className={inputClass}
              data-testid="save-as-input"
            />
            <p className="text-xs text-slate-500">A friendly name to identify this entry</p>
          </div>

          {/* Bank Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Name of Bank *</Label>
              <Input
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                placeholder="e.g., HDFC Bank"
                className={inputClass}
                data-testid="bank-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>IFSC Code</Label>
              <Input
                name="ifsc_code"
                value={formData.ifsc_code}
                onChange={handleChange}
                placeholder="e.g., HDFC0001234"
                maxLength={11}
                className={`${inputClass} uppercase`}
                data-testid="ifsc-code-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Account Number</Label>
            <Input
              name="account_number"
              value={formData.account_number}
              onChange={handleChange}
              placeholder="Enter account number"
              className={inputClass}
              data-testid="account-number-input"
            />
          </div>

          {/* Joint Holders */}
          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Joint Holders (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Joint Holder 1</Label>
                <Input
                  name="joint_holder_1"
                  value={formData.joint_holder_1}
                  onChange={handleChange}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Joint Holder 2</Label>
                <Input
                  name="joint_holder_2"
                  value={formData.joint_holder_2}
                  onChange={handleChange}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Nominees */}
          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Nominees (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Nominee 1</Label>
                <Input
                  name="nominee_1"
                  value={formData.nominee_1}
                  onChange={handleChange}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Nominee 2</Label>
                <Input
                  name="nominee_2"
                  value={formData.nominee_2}
                  onChange={handleChange}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Nominee 3</Label>
                <Input
                  name="nominee_3"
                  value={formData.nominee_3}
                  onChange={handleChange}
                  placeholder="Full name"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Contact Person */}
          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Bank Contact (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Contact Person Name</Label>
                <Input
                  name="contact_person_name"
                  value={formData.contact_person_name}
                  onChange={handleChange}
                  placeholder="e.g., Mr. Sharma"
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Contact Person Mobile</Label>
                <Input
                  name="contact_person_mobile"
                  value={formData.contact_person_mobile}
                  onChange={handleChange}
                  placeholder="e.g., 9876543210"
                  maxLength={10}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            data-testid="save-entry-btn"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" /> Saving...
              </>
            ) : (
              <>
                {mode === 'add' ? 'Add Entry' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BankEntryModal;
